import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Role';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const ROLE_API_REDUCER_KEY = 'roles';
export const ROLE_PATH = 'roles';

export const roleApi = createApi({
    reducerPath: ROLE_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Role', 'Roles'],
    endpoints: (builder) => ({
        getRoleList: builder.query<PaginationResult<Entity.Role>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ ROLE_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Roles'],
        }),
        getRole: builder.query<Entity.Role, Entity.PrimaryRole>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = roleNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ ROLE_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Role'],
        }),
        getRoleMultiple: builder.query<Entity.Role, Entity.PrimaryRole[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROLE_PATH }/ids${Entity.PrimaryRole.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Roles'],
        }),
        getRolePresentation: builder.query<PaginationResult<Entity.Role>, Entity.PresentationRoleRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ ROLE_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Roles'],
        }),
        getRoleAdvancedPresentation: builder.query<PaginationResult<Entity.Role>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ ROLE_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Roles'],
        }),
        
        insertRole: builder.mutation<Entity.Role, Entity.CreateRole>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROLE_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    roleApi.util.upsertQueryData('getRole', Entity.PrimaryRole.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Roles'],
        }),
        insertMultipleRole: builder.mutation<Entity.Role[], Entity.CreateRole[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROLE_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            roleApi.util.upsertQueryData('getRole', Entity.PrimaryRole.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Roles'],
        }),
        updateRole: builder.mutation<Entity.Role, Entity.UpdateEntityRole>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ROLE_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Roles'],
        }),
        updateMultipleRole: builder.mutation<Entity.Role[], Entity.UpdateEntityRole[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ROLE_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Roles'],
        }),
        deleteRole: builder.mutation<Entity.Role, Entity.Role>({
            query: (toDelete) => {
                return ({
                    url: `/${ ROLE_PATH }/${Entity.PrimaryRole.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Roles'],
        }),
        deleteMultipleRole: builder.mutation<Entity.Role[], Entity.PrimaryRole[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROLE_PATH }/batch${Entity.PrimaryRole.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Roles'],
        }),
    }),
})

export const [roleNormalizer, roleNormalizerMiddleware] = createNormalizationMiddleware(ROLE_API_REDUCER_KEY, roleApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetRoleListQuery,
    useGetRoleMultipleQuery,
    useGetRolePresentationQuery,
    useGetRoleQuery,
    useInsertRoleMutation,
    useInsertMultipleRoleMutation,
    useUpdateRoleMutation,
    useUpdateMultipleRoleMutation,
    useDeleteRoleMutation,
    useDeleteMultipleRoleMutation,
    useGetRoleAdvancedPresentationQuery,
    
} = roleApi
